// Imports
import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components/macro';
import Checkbox from '../../../../../../../shareables/foundation/front-end/components/forms/fields/checkbox';
import Radio from '../../../../../../../shareables/foundation/front-end/components/forms/fields/radio';
import MultiCheckboxContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-checkbox-container';
import commonActions from '../../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetContactResponse } from '../../../../../../../shareables/types/api/main/website/member/settings/contact/get';
import { PutContactParameters } from '../../../../../../../shareables/types/api/main/website/member/settings/contact/put';
import { GeneratedThemeProps } from '../../../../../../templates/template-style';


// Styled components
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	line-height: 1.3125rem;
`;

const RadioContainer = styled.div`
	margin-bottom: 1.25rem;
`;

const EmailFrequencyNote = styled.small`
	font-weight: normal;
`;


// Define the accepted props
interface Props {
	storybookContactSettings?: GetContactResponse;
}


// Function component
const Contact: React.FC<Props> = ({ storybookContactSettings }) => {
	// Use state
	const [contactSettings, setContactSettings] = useState<GetContactResponse | null>(storybookContactSettings ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use ref
	const ref = useRef<HTMLDivElement | null>(null);
	
	
	// Use React Router functionality
	const updateHeight: (newHeight: number) => void = useOutletContext();
	
	
	// Update height for the members settings page
	useEffect(() => {
		if (!storybookContactSettings && ref.current) {
			updateHeight(ref.current.clientHeight);
		}
	}, [updateHeight, contactSettings, storybookContactSettings]);
	
	
	// Load contact settings on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetContactResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/member/settings/contact`,
				completion: (json) => {
					setContactSettings(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// If loading, then display nothing
	if (contactSettings === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<PutContactParameters, undefined, undefined> = {
		action: 'PUT',
		uri: `/website/${window.location.hostname}/member/settings/contact`,
		submitButton: {
			text: 'Save',
		},
		recoverState: false,
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		dispatch(
			commonActions.showPageMessage({
				color: 'success',
				title: 'Success',
				message: 'These settings have been saved.',
			})
		);
	};
	
	
	// Return JSX
	return (
		<Container ref={ref}>
			<APIForm options={formOptions} onCompletion={handleFormCompletion}>
				<MultiCheckboxContainer>
					<h4>Receive these types of emails…</h4>
					<Checkbox
						name='receiveNotifications'
						label='Notification emails'
						note={<span className='is-gray'>Forum replies, contact forms, etc.</span>}
						initiallyChecked={contactSettings.receiveNotifications}
					/>
					<Checkbox
						name='receiveEventReminders'
						label='Event reminders'
						note={<span className='is-gray'>Event-specific reminders configured by admins</span>}
						initiallyChecked={contactSettings.receiveEventReminders}
					/>
					<Checkbox
						name='receiveBlasts'
						label='Emails from administrators'
						note={<span className='is-gray'>Newsletters, announcements, etc.</span>}
						initiallyChecked={contactSettings.receiveBlasts}
					/>
					<Checkbox
						name='receiveAlerts'
						label='Alerts from residents via the alert page'
						note={<span className='is-gray'>Burglaries, warnings, etc.</span>}
						initiallyChecked={contactSettings.receiveAlerts}
					/>
					<Checkbox
						name='receiveAdmin'
						label='Administrative communications'
						note={<span className='is-gray'>Only if you are an admin</span>}
						initiallyChecked={contactSettings.receiveAdmin}
					/>
				</MultiCheckboxContainer>
				
				<MultiCheckboxContainer>
					<h4>Receive these types of text messages…</h4>
					<Checkbox
						name='receiveSMSs'
						label='Text messages from administrators'
						note={<span className='is-gray'>Announcements, alerts, etc.</span>}
						initiallyChecked={contactSettings.receiveSMSs}
					/>
					<small className='is-gray'>
						<em>
							Most communities send no more than a few messages per month. Message and data rates may apply. Text HELP
							for help. Text STOP to cancel. See our {/* eslint-disable-next-line react/jsx-no-target-blank */}
							<a
								target='_blank'
								href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME ?? ''}/policies/terms-of-service`}
							>
								Terms of Service
							</a>{' '}
							and {/* eslint-disable-next-line react/jsx-no-target-blank */}
							<a
								target='_blank'
								href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME ?? ''}/policies/privacy-policy`}
							>
								Privacy Policy.
							</a>
						</em>
					</small>
				</MultiCheckboxContainer>
				
				<MultiCheckboxContainer>
					<h4>Receive instant email notifications for…</h4>
					<Checkbox
						name='receiveInstantForumTopic'
						label='New forum topics'
						initiallyChecked={contactSettings.receiveInstantForumTopic}
					/>
					<Checkbox
						name='receiveInstantForumComment'
						label='New forum comments on topics'
						initiallyChecked={contactSettings.receiveInstantForumComment}
					/>
				</MultiCheckboxContainer>
				
				<RadioContainer>
					<h4>
						What’s Happening email frequency{' '}
						<EmailFrequencyNote className='is-gray'>
							<em>Recent activity + upcoming events</em>
						</EmailFrequencyNote>
					</h4>
					<Radio
						name='receiveWhatsHappening'
						initialValue={contactSettings.receiveWhatsHappening}
						choices={[
							{
								label: (
									<React.Fragment>
										Recommended<small className='is-gray'>Chosen by website admin</small>
									</React.Fragment>
								),
								value: 'RECOMMENDED',
							},
							{
								label: 'Daily',
								value: 'DAILY',
							},
							{
								label: 'Weekly',
								value: 'WEEKLY',
							},
							{
								label: 'Biweekly',
								value: 'BIWEEKLY',
							},
							{
								label: 'Monthly',
								value: 'MONTHLY',
							},
							{
								label: 'Never',
								value: 'NEVER',
							},
						]}
					/>
				</RadioContainer>
			</APIForm>
		</Container>
	);
};

export default Contact;
